<template>
	<div class="sr" @click="$emit('onPosterClickOut')">
		<div class="sr__container radius-xsmall border-100 border-high-active bg-low-active">
			<div class="sr__overlay sr__overlay--gradient px-5 py-7">
				<div class="sr__content-container g-1">
					<slot name="provider-icon"></slot>

					<slot name="campaign-info"></slot>

					<slot name="action-items"></slot>

					<div v-if="sponsoredAdCampaign?.disclaimerText" class="sr__disclaimer pt-1">
						{{ sponsoredAdCampaign?.disclaimerText }}
					</div>
				</div>
			</div>

			<div class="sr__image-container">
				<picture>
					<source
						type="image/avif"
						:srcset="sponsoredAdCampaign?.promotionalImageUrl?.replace(/\.\w+$/, '.avif')"
					/>

					<img
						class="sr__img sponsored-recommendation__img"
						:alt="campaignNode?.content.title"
						:src="sponsoredAdCampaign?.promotionalImageUrl ?? ''"
						loading="eager"
						data-testid="sr__imgContainer"
						@imageError="$emit('imageError', sponsoredAdCampaign?.promotionalImageUrl)"
					/>
				</picture>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import type { Maybe } from '@/helpers/types';

import { computed } from 'vue';
import { SingleTitleSR } from '@/features/sponsored-recommendations/types';

import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';

const props = withDefaults(
	defineProps<{
		sponsoredAdCampaign: NonNullable<SponsoredAdFragment['campaign']> & { node: SingleTitleSR };
		lazy?: boolean;
		additionalContexts?: SnowplowContext[];
		isCinemaSR: boolean;
	}>(),
	{
		lazy: true,
		isCinemaSR: false,
		additionalContexts: () => [],
	}
);

defineEmits<{
	(e: 'onPosterClickOut'): void;
	(e: 'imageError', src: Maybe<string>): void;
}>();

const campaignNode = computed(() => props.sponsoredAdCampaign.node);
</script>

<style lang="scss" scoped>
.sr {
	position: relative;
	height: 100%;
	container: sponsoredRecommendation / inline-size;
	cursor: pointer;

	&__container {
		position: relative;
		overflow: hidden;
		transition: opacity 0.1s ease;
		width: 100%;
		height: 100%;
		display: flex;
	}

	&__overlay {
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		position: relative;
		z-index: 1;
		width: 100%;

		&--gradient {
			background: linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.6) 60%, rgba(0, 0, 0, 0.9));
		}
	}

	&__image-container {
		height: 100%;
		width: 100%;
		position: absolute;
		top: 0;
		left: 0;
		aspect-ratio: 1.5/1;
	}

	&__img {
		display: inline-block;
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: top;
	}

	&__content-container {
		display: flex;
		flex-direction: column;
	}

	&__disclaimer {
		font-size: 10px;
		color: var(--jw-shade-white);
	}

	.provider-icon,
	:deep(.provider-icon) {
		flex-shrink: 0;
		width: var(--jw-size-8);
		height: var(--jw-size-8);
	}

	:deep(.picture-comp__img) {
		object-fit: cover;
	}

	@container srCompact (max-width: 360px) {
		&__content-container {
			gap: var(--jw-spacing-half);
		}

		::v-deep(.action-items__providerIcon img.provider-icon) {
			width: var(--jw-size-5);
			height: var(--jw-size-5);
			min-width: var(--jw-size-5);
		}
	}
}

//remove opacity on ios devices as it causes flickering
html.ios .sr__poster:active {
	opacity: unset;
}
</style>
